import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, mergeProps as _mergeProps } from "vue"

const _hoisted_1 = { key: 0 }
const _hoisted_2 = {
  key: 1,
  class: "text-grey"
}
const _hoisted_3 = { class: "text-caption text-medium-emphasis" }
const _hoisted_4 = { class: "text-h5" }

import { reactive, ref, onMounted, computed } from "vue";
import { apiQuery } from "@/api/j24api";

// Interface definitions
interface MdmServer {
  _id: string;
  name: string;
  url: string;
  type: string;
  customerID: string;
}

interface JamfGroup {
  id: string;
  name: string;
  size?: number;
}

interface SchedulerJob {
  _id?: string;
  name: string;
  description?: string;
  mdmServerId: string;
  mdmServerName?: string;
  jamfGroupId: string;
  jamfGroupName?: string;
  command: string;
  scheduleType: "interval" | "fixed"; // Type of scheduling
  offset: number; // Offset time in minutes (for interval)
  fixedTime?: string; // Fixed time in HH:MM format (for fixed)
  timezone?: string; // Timezone for fixed time
  wallpaper_content?: string; // Base64 encoded PNG for wallpaper command
  active: boolean;
  createdAt?: Date;
  createdBy?: string;
  nextRunTime?: Date;
  lastRunTime?: Date;
  runCount?: number;
  lastError?: string;
  lastErrorTime?: Date;
}

// Reactive data

export default /*@__PURE__*/_defineComponent({
  __name: 'DeviceGroupScheduler',
  setup(__props) {

const jobs = reactive<SchedulerJob[]>([]);
const mdmServers = reactive<MdmServer[]>([]);
const jamfGroups = reactive<JamfGroup[]>([]);

// Refs
const loading = ref(false);
const loadingGroups = ref(false);
const saving = ref(false);
const deleting = ref(false);
const search = ref("");
const alert = ref("");
const wallpaperFile = ref<File | null>(null);

// Dialog states
const showJobDialog = ref(false);
const showDeleteDialog = ref(false);
const isEditing = ref(false);

// Current job being edited/created
const currentJob = ref<SchedulerJob>({
  name: "",
  description: "",
  mdmServerId: "",
  jamfGroupId: "",
  command: "",
  scheduleType: "interval", // Default to interval scheduling
  offset: 15, // Default to 15 minutes
  fixedTime: "",
  timezone: "Europe/London", // Default to UK timezone
  active: true,
});

const jobToDelete = ref<SchedulerJob | null>(null);

// Table headers
const headers = [
  { title: "Name", key: "name", sortable: true },
  { title: "Server", key: "mdmServerName", sortable: true },
  { title: "Command", key: "command", sortable: true },
  { title: "Schedule", key: "scheduleType", sortable: true },
  { title: "Status", key: "active", sortable: true },
  { title: "Last Run", key: "lastRunTime", sortable: true },
  { title: "Next Run", key: "nextRunTime", sortable: true },
  { title: "Runs", key: "runCount", sortable: true },
  { title: "Error", key: "lastError", sortable: false },
  { title: "Actions", key: "actions", sortable: false },
];

// Available commands for mobile devices
const availableCommands = [
  "UpdateInventory",
  "BlankPush",
  "Wallpaper",
  // "EraseDevice",
  // "EnableLostMode",
  // "DisableLostMode",
  "DeviceLock",
  "ClearPasscode",
  "RestartDevice",
  // "ShutDownDevice",
];

// Schedule type options
const scheduleTypeOptions = [
  { title: "Interval (every X minutes)", value: "interval" },
  { title: "Fixed Time (daily at specific time)", value: "fixed" },
];

// Offset time options (in 15-minute increments)
const offsetOptions = [
  { title: "15 minutes", value: 15 },
  { title: "30 minutes", value: 30 },
  { title: "45 minutes", value: 45 },
  { title: "1 hour", value: 60 },
  { title: "1 hour 15 minutes", value: 75 },
  { title: "1 hour 30 minutes", value: 90 },
  { title: "1 hour 45 minutes", value: 105 },
  { title: "2 hours", value: 120 },
  { title: "3 hours", value: 180 },
  { title: "4 hours", value: 240 },
  { title: "6 hours", value: 360 },
  { title: "8 hours", value: 480 },
  { title: "12 hours", value: 720 },
  { title: "24 hours", value: 1440 },
];

// Timezone options (most common timezones)
const timezoneOptions = [
  { title: "Europe/London (GMT/BST) - UK", value: "Europe/London" },
  { title: "UTC", value: "UTC" },
  { title: "America/New_York (EST/EDT)", value: "America/New_York" },
  { title: "America/Chicago (CST/CDT)", value: "America/Chicago" },
  { title: "America/Denver (MST/MDT)", value: "America/Denver" },
  { title: "America/Los_Angeles (PST/PDT)", value: "America/Los_Angeles" },
  { title: "Europe/Paris (CET/CEST)", value: "Europe/Paris" },
  { title: "Asia/Tokyo (JST)", value: "Asia/Tokyo" },
  { title: "Asia/Shanghai (CST)", value: "Asia/Shanghai" },
  { title: "Australia/Sydney (AEST/AEDT)", value: "Australia/Sydney" },
];

// Validation rules
const rules = {
  required: (value: any) => !!value || "This field is required",
  pngOnly: (value: any) => {
    if (!value) return "This field is required";

    // Handle different input types from v-file-input
    let file: File | null = null;

    if (Array.isArray(value) && value.length > 0) {
      file = value[0];
    } else if (value instanceof File) {
      file = value;
    } else {
      return "This field is required";
    }

    if (!file || !file.type) return "Invalid file";
    return file.type === "image/png" || "Only PNG files are allowed";
  },
};

// Computed properties
const isFormValid = computed(() => {
  const baseValid =
    currentJob.value.name &&
    currentJob.value.mdmServerId &&
    currentJob.value.jamfGroupId &&
    currentJob.value.command &&
    currentJob.value.scheduleType;

  // Schedule type specific validation
  let scheduleValid = true;
  if (currentJob.value.scheduleType === "interval") {
    scheduleValid = !!currentJob.value.offset;
  } else if (currentJob.value.scheduleType === "fixed") {
    scheduleValid = !!currentJob.value.fixedTime;
  }

  // If wallpaper command is selected, also check for wallpaper file
  if (currentJob.value.command === "Wallpaper") {
    // Check if we have a wallpaper file (could be array with files or single file)
    const hasWallpaperFile =
      wallpaperFile.value &&
      ((Array.isArray(wallpaperFile.value) && wallpaperFile.value.length > 0) ||
        wallpaperFile.value instanceof File ||
        currentJob.value.wallpaper_content); // Or if base64 content is already set
    return baseValid && scheduleValid && hasWallpaperFile;
  }

  return baseValid && scheduleValid;
});

// Methods
function handleWallpaperFileChange(files: any) {
  let file: File | null = null;

  // Handle different input types from v-file-input
  if (Array.isArray(files) && files.length > 0) {
    file = files[0];
  } else if (files instanceof File) {
    file = files;
  }

  if (file && file.type === "image/png") {
    convertFileToBase64(file);
  } else {
    currentJob.value.wallpaper_content = undefined;
  }
}

function convertFileToBase64(file: File) {
  const reader = new FileReader();
  reader.onload = (event) => {
    if (event.target?.result) {
      // Remove the data:image/png;base64, prefix to get just the base64 data
      const base64String = (event.target.result as string).split(",")[1];
      currentJob.value.wallpaper_content = base64String;
    }
  };
  reader.readAsDataURL(file);
}

async function loadJobs() {
  loading.value = true;
  try {
    console.log("Loading jobs...");

    // Try the current approach first
    let result = await apiQuery("ELA", "scheduler/jobs", {}, "GET");

    // If that doesn't work, try the alternative approach
    if (!result || !Array.isArray(result)) {
      console.log("Trying alternative API call...");
      result = await apiQuery("ELA", "scheduler/jobs", {
        apiAction: "get",
      });
    }

    console.log("Jobs loaded:", result);

    // Clear existing jobs
    jobs.length = 0;

    if (result && Array.isArray(result)) {
      // Enrich jobs with server names
      result.forEach((job) => {
        const server = mdmServers.find((s) => s._id === job.mdmServerId);
        job.mdmServerName = server?.name || "Unknown Server";
        jobs.push(job);
      });
      console.log("Jobs array updated, length:", jobs.length);
    } else {
      console.warn("No jobs returned or invalid format:", result);
    }
  } catch (error) {
    console.error("Error loading jobs:", error);
    alert.value = "Error loading scheduler jobs";
  } finally {
    loading.value = false;
  }
}

async function loadMdmServers() {
  try {
    const result = await apiQuery("J24Auth", "elaadmin/mdmservers", {
      apiAction: "get",
    });

    if (result && Array.isArray(result)) {
      mdmServers.length = 0;
      // Filter for Jamf servers only
      result
        .filter((server) => server.type === "Jamf")
        .forEach((server) => {
          mdmServers.push(server);
        });
    }
  } catch (error) {
    console.error("Error loading MDM servers:", error);
    alert.value = "Error loading MDM servers";
  }
}

async function loadJamfGroups() {
  if (!currentJob.value.mdmServerId) {
    jamfGroups.length = 0;
    return;
  }

  loadingGroups.value = true;
  try {
    // Use the existing Jamf search functionality to get mobile device groups
    const result = await apiQuery("ELA", "scheduler/groups", {
      mdm: "Jamf",
      command: "getSmartGroups",
      mdmServerId: currentJob.value.mdmServerId,
    });

    // const result = await apiQuery("ELA", "dashboard/jamfsearch", {
    //   mdmServerId: currentJob.value.mdmServerId,
    //   firstObject: {
    //     resource: "/JSSResource/mobiledevicegroups",
    //     identifier: "id",
    //   },
    //   secondObject: {
    //     resource: "/JSSResource/mobiledevicegroups/id/{id}",
    //     matchField: "mobile_device_group/name",
    //     matchValue: "", // Empty to get all groups
    //   },
    // });

    console.log("Jamf groups result:", result);

    jamfGroups.length = 0;
    if (result.mergedGroups && Array.isArray(result.mergedGroups)) {
      result.mergedGroups.forEach((group) => {
        jamfGroups.push({
          id: group.id?.toString() || "",
          name: group.name || `Group ${group.id}`,
          size: group.size,
        });
      });
    }
  } catch (error) {
    console.error("Error loading Jamf groups:", error);
    alert.value = "Error loading Jamf device groups";
  }
  loadingGroups.value = false;
}

function addNewJob() {
  currentJob.value = {
    name: "",
    description: "",
    mdmServerId: "",
    jamfGroupId: "",
    command: "",
    scheduleType: "interval", // Default to interval scheduling
    offset: 15, // Default to 15 minutes
    fixedTime: "",
    timezone: "Europe/London", // Default to UK timezone
    active: true,
  };
  wallpaperFile.value = null;
  isEditing.value = false;
  showJobDialog.value = true;
}

function editJob(job: SchedulerJob) {
  currentJob.value = { ...job };
  isEditing.value = true;
  showJobDialog.value = true;

  // Load groups for the selected server
  if (job.mdmServerId) {
    loadJamfGroups();
  }
}

async function saveJob() {
  if (!isFormValid.value) return;

  saving.value = true;
  try {
    const endpoint = `scheduler/jobs`;
    const method = isEditing.value ? "PUT" : "POST";

    if (isEditing.value) {
      currentJob.value.id = currentJob.value._id || "";
    } else {
      currentJob.value.id = ""; // Clear ID for new job
    }

    await apiQuery("ELA", endpoint, currentJob.value, method);

    showJobDialog.value = false;
    alert.value = `Job ${isEditing.value ? "updated" : "created"} successfully`;

    // Small delay to ensure backend has processed the update
    await new Promise((resolve) => setTimeout(resolve, 500));
    await loadJobs();
  } catch (error) {
    console.error("Error saving job:", error);
    alert.value = `Error ${isEditing.value ? "updating" : "creating"} job`;
  }
  saving.value = false;
}

function deleteJob(job: SchedulerJob) {
  jobToDelete.value = job;
  showDeleteDialog.value = true;
}

async function confirmDelete() {
  if (!jobToDelete.value?._id) return;

  deleting.value = true;
  try {
    console.log("Deleting job:", jobToDelete.value);
    console.log("Job ID:", jobToDelete.value._id);
    await apiQuery(
      "ELA",
      `scheduler/jobs`,
      { id: jobToDelete.value._id },
      "DELETE"
    );

    showDeleteDialog.value = false;
    alert.value = "Job deleted successfully";
    await new Promise((resolve) => setTimeout(resolve, 500));
    await loadJobs();
  } catch (error) {
    console.error("Error deleting job:", error);
    alert.value = "Error deleting job";
  }
  deleting.value = false;
}

async function toggleJobStatus(job: SchedulerJob) {
  try {
    const updatedJob = { ...job, active: !job.active, id: job._id };
    await apiQuery("ELA", `scheduler/jobs`, updatedJob, "PUT");

    alert.value = `Job ${updatedJob.active ? "activated" : "deactivated"}`;

    // Small delay to ensure backend has processed the update
    await new Promise((resolve) => setTimeout(resolve, 500));
    await loadJobs();
  } catch (error) {
    console.error("Error toggling job status:", error);
    alert.value = "Error updating job status";
  }
}

function closeDialog() {
  showJobDialog.value = false;
  jamfGroups.length = 0;
  wallpaperFile.value = null;
}

function formatDate(dateString: string | Date) {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleString();
}

// Lifecycle
onMounted(async () => {
  await loadMdmServers();
  await loadJobs();
});

return (_ctx: any,_cache: any) => {
  const _component_v_col = _resolveComponent("v-col")!
  const _component_v_row = _resolveComponent("v-row")!
  const _component_v_alert = _resolveComponent("v-alert")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_text_field = _resolveComponent("v-text-field")!
  const _component_v_card_title = _resolveComponent("v-card-title")!
  const _component_v_chip = _resolveComponent("v-chip")!
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_tooltip = _resolveComponent("v-tooltip")!
  const _component_v_data_table = _resolveComponent("v-data-table")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_select = _resolveComponent("v-select")!
  const _component_v_textarea = _resolveComponent("v-textarea")!
  const _component_v_file_input = _resolveComponent("v-file-input")!
  const _component_v_switch = _resolveComponent("v-switch")!
  const _component_v_container = _resolveComponent("v-container")!
  const _component_v_card_text = _resolveComponent("v-card-text")!
  const _component_v_spacer = _resolveComponent("v-spacer")!
  const _component_v_card_actions = _resolveComponent("v-card-actions")!
  const _component_v_dialog = _resolveComponent("v-dialog")!

  return (_openBlock(), _createBlock(_component_v_container, { fluid: "" }, {
    default: _withCtx(() => [
      _createVNode(_component_v_row, null, {
        default: _withCtx(() => [
          _createVNode(_component_v_col, { cols: "12" }, {
            default: _withCtx(() => _cache[16] || (_cache[16] = [
              _createElementVNode("h1", { class: "text-h4 mb-4" }, "Device Group Scheduler", -1),
              _createElementVNode("p", { class: "text-body-1 mb-6" }, " Manage scheduled commands for Jamf device groups. Commands will execute automatically at configurable intervals (15 minutes to 24 hours). ", -1)
            ])),
            _: 1
          })
        ]),
        _: 1
      }),
      (alert.value)
        ? (_openBlock(), _createBlock(_component_v_row, { key: 0 }, {
            default: _withCtx(() => [
              _createVNode(_component_v_col, { cols: "12" }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_alert, {
                    type: "info",
                    dismissible: "",
                    onClick: _cache[0] || (_cache[0] = ($event: any) => (alert.value = ''))
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(alert.value), 1)
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              })
            ]),
            _: 1
          }))
        : _createCommentVNode("", true),
      _createVNode(_component_v_row, { class: "mb-4" }, {
        default: _withCtx(() => [
          _createVNode(_component_v_col, {
            cols: "12",
            class: "d-flex justify-end"
          }, {
            default: _withCtx(() => [
              _createVNode(_component_v_btn, {
                color: "primary",
                onClick: addNewJob,
                "prepend-icon": "mdi-plus"
              }, {
                default: _withCtx(() => _cache[17] || (_cache[17] = [
                  _createTextVNode(" Add New Schedule ")
                ])),
                _: 1
              })
            ]),
            _: 1
          })
        ]),
        _: 1
      }),
      _createVNode(_component_v_row, null, {
        default: _withCtx(() => [
          _createVNode(_component_v_col, { cols: "12" }, {
            default: _withCtx(() => [
              _createVNode(_component_v_card, null, {
                default: _withCtx(() => [
                  _createVNode(_component_v_card_title, null, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_text_field, {
                        modelValue: search.value,
                        "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((search).value = $event)),
                        "prepend-inner-icon": "mdi-magnify",
                        label: "Search jobs...",
                        "single-line": "",
                        "hide-details": "",
                        clearable: ""
                      }, null, 8, ["modelValue"])
                    ]),
                    _: 1
                  }),
                  _createVNode(_component_v_data_table, {
                    headers: headers,
                    items: jobs,
                    search: search.value,
                    loading: loading.value,
                    class: "elevation-1",
                    "item-key": "_id"
                  }, {
                    [`item.mdmServerName`]: _withCtx(({ item }) => [
                      _createVNode(_component_v_chip, {
                        color: item.active ? 'success' : 'grey',
                        size: "small",
                        class: "mr-2"
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(item.mdmServerName || "Unknown Server"), 1)
                        ]),
                        _: 2
                      }, 1032, ["color"])
                    ]),
                    [`item.active`]: _withCtx(({ item }) => [
                      _createVNode(_component_v_chip, {
                        color: item.active ? 'success' : 'error',
                        size: "small"
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(item.active ? "Active" : "Inactive"), 1)
                        ]),
                        _: 2
                      }, 1032, ["color"])
                    ]),
                    [`item.lastRunTime`]: _withCtx(({ item }) => [
                      (item.lastRunTime)
                        ? (_openBlock(), _createElementBlock("span", _hoisted_1, _toDisplayString(formatDate(item.lastRunTime)), 1))
                        : (_openBlock(), _createElementBlock("span", _hoisted_2, "Never"))
                    ]),
                    [`item.nextRunTime`]: _withCtx(({ item }) => [
                      _createElementVNode("span", null, _toDisplayString(formatDate(item.nextRunTime)), 1)
                    ]),
                    [`item.scheduleType`]: _withCtx(({ item }) => [
                      _createElementVNode("div", null, [
                        _createVNode(_component_v_chip, {
                          size: "small",
                          variant: "outlined",
                          color: item.scheduleType === 'fixed' ? 'primary' : 'info'
                        }, {
                          default: _withCtx(() => [
                            _createTextVNode(_toDisplayString(item.scheduleType === "fixed" ? "Fixed" : "Interval"), 1)
                          ]),
                          _: 2
                        }, 1032, ["color"]),
                        _createElementVNode("div", _hoisted_3, _toDisplayString(item.scheduleType === "fixed"
                      ? item.fixedTime
                      : `${item.offset || 15}m`), 1)
                      ])
                    ]),
                    [`item.runCount`]: _withCtx(({ item }) => [
                      _createVNode(_component_v_chip, {
                        size: "small",
                        variant: "outlined"
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(item.runCount || 0), 1)
                        ]),
                        _: 2
                      }, 1024)
                    ]),
                    [`item.actions`]: _withCtx(({ item }) => [
                      _createVNode(_component_v_btn, {
                        size: "small",
                        color: "primary",
                        variant: "text",
                        icon: "mdi-pencil",
                        onClick: ($event: any) => (editJob(item))
                      }, null, 8, ["onClick"]),
                      _createVNode(_component_v_btn, {
                        size: "small",
                        color: "error",
                        variant: "text",
                        icon: "mdi-delete",
                        onClick: ($event: any) => (deleteJob(item))
                      }, null, 8, ["onClick"]),
                      _createVNode(_component_v_btn, {
                        size: "small",
                        color: item.active ? 'warning' : 'success',
                        variant: "text",
                        icon: item.active ? 'mdi-pause' : 'mdi-play',
                        onClick: ($event: any) => (toggleJobStatus(item))
                      }, null, 8, ["color", "icon", "onClick"])
                    ]),
                    [`item.lastError`]: _withCtx(({ item }) => [
                      (item.lastError)
                        ? (_openBlock(), _createBlock(_component_v_tooltip, {
                            key: 0,
                            location: "top"
                          }, {
                            activator: _withCtx(({ props }) => [
                              _createVNode(_component_v_icon, _mergeProps(props, {
                                color: "error",
                                size: "small"
                              }), {
                                default: _withCtx(() => _cache[18] || (_cache[18] = [
                                  _createTextVNode("mdi-alert-circle")
                                ])),
                                _: 2
                              }, 1040)
                            ]),
                            default: _withCtx(() => [
                              _createElementVNode("span", null, _toDisplayString(item.lastError), 1)
                            ]),
                            _: 2
                          }, 1024))
                        : (_openBlock(), _createBlock(_component_v_icon, {
                            key: 1,
                            color: "success",
                            size: "small"
                          }, {
                            default: _withCtx(() => _cache[19] || (_cache[19] = [
                              _createTextVNode("mdi-check-circle")
                            ])),
                            _: 1
                          }))
                    ]),
                    _: 2
                  }, 1032, ["items", "search", "loading"])
                ]),
                _: 1
              })
            ]),
            _: 1
          })
        ]),
        _: 1
      }),
      _createVNode(_component_v_dialog, {
        modelValue: showJobDialog.value,
        "onUpdate:modelValue": _cache[13] || (_cache[13] = ($event: any) => ((showJobDialog).value = $event)),
        "max-width": "800px",
        persistent: ""
      }, {
        default: _withCtx(() => [
          _createVNode(_component_v_card, null, {
            default: _withCtx(() => [
              _createVNode(_component_v_card_title, null, {
                default: _withCtx(() => [
                  _createElementVNode("span", _hoisted_4, _toDisplayString(isEditing.value ? "Edit" : "Add") + " Scheduler Job", 1)
                ]),
                _: 1
              }),
              _createVNode(_component_v_card_text, null, {
                default: _withCtx(() => [
                  _createVNode(_component_v_container, null, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_row, null, {
                        default: _withCtx(() => [
                          _createVNode(_component_v_col, {
                            cols: "12",
                            md: "6"
                          }, {
                            default: _withCtx(() => [
                              _createVNode(_component_v_text_field, {
                                modelValue: currentJob.value.name,
                                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((currentJob.value.name) = $event)),
                                label: "Job Name*",
                                rules: [rules.required],
                                required: ""
                              }, null, 8, ["modelValue", "rules"])
                            ]),
                            _: 1
                          }),
                          _createVNode(_component_v_col, {
                            cols: "12",
                            md: "6"
                          }, {
                            default: _withCtx(() => [
                              _createVNode(_component_v_select, {
                                modelValue: currentJob.value.mdmServerId,
                                "onUpdate:modelValue": [
                                  _cache[3] || (_cache[3] = ($event: any) => ((currentJob.value.mdmServerId) = $event)),
                                  loadJamfGroups
                                ],
                                items: mdmServers,
                                "item-title": "name",
                                "item-value": "_id",
                                label: "MDM Server*",
                                rules: [rules.required],
                                required: ""
                              }, null, 8, ["modelValue", "items", "rules"])
                            ]),
                            _: 1
                          }),
                          _createVNode(_component_v_col, { cols: "12" }, {
                            default: _withCtx(() => [
                              _createVNode(_component_v_textarea, {
                                modelValue: currentJob.value.description,
                                "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((currentJob.value.description) = $event)),
                                label: "Description",
                                rows: "2"
                              }, null, 8, ["modelValue"])
                            ]),
                            _: 1
                          }),
                          _createVNode(_component_v_col, {
                            cols: "12",
                            md: "6"
                          }, {
                            default: _withCtx(() => [
                              _createVNode(_component_v_select, {
                                modelValue: currentJob.value.jamfGroupId,
                                "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((currentJob.value.jamfGroupId) = $event)),
                                items: jamfGroups,
                                "item-title": "name",
                                "item-value": "id",
                                label: "Jamf Device Group*",
                                rules: [rules.required],
                                loading: loadingGroups.value,
                                disabled: !currentJob.value.mdmServerId,
                                required: ""
                              }, null, 8, ["modelValue", "items", "rules", "loading", "disabled"])
                            ]),
                            _: 1
                          }),
                          _createVNode(_component_v_col, {
                            cols: "12",
                            md: "6"
                          }, {
                            default: _withCtx(() => [
                              _createVNode(_component_v_select, {
                                modelValue: currentJob.value.command,
                                "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((currentJob.value.command) = $event)),
                                items: availableCommands,
                                label: "Command*",
                                rules: [rules.required],
                                required: ""
                              }, null, 8, ["modelValue", "rules"])
                            ]),
                            _: 1
                          }),
                          _createVNode(_component_v_col, {
                            cols: "12",
                            md: "6"
                          }, {
                            default: _withCtx(() => [
                              _createVNode(_component_v_select, {
                                modelValue: currentJob.value.scheduleType,
                                "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((currentJob.value.scheduleType) = $event)),
                                items: scheduleTypeOptions,
                                label: "Schedule Type*",
                                rules: [rules.required],
                                required: "",
                                hint: "Choose between interval or fixed time scheduling",
                                "persistent-hint": ""
                              }, null, 8, ["modelValue", "rules"])
                            ]),
                            _: 1
                          }),
                          (currentJob.value.scheduleType === 'interval')
                            ? (_openBlock(), _createBlock(_component_v_col, {
                                key: 0,
                                cols: "12",
                                md: "6"
                              }, {
                                default: _withCtx(() => [
                                  _createVNode(_component_v_select, {
                                    modelValue: currentJob.value.offset,
                                    "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((currentJob.value.offset) = $event)),
                                    items: offsetOptions,
                                    label: "Execution Interval*",
                                    rules: [rules.required],
                                    required: "",
                                    hint: "How often this job should run",
                                    "persistent-hint": ""
                                  }, null, 8, ["modelValue", "rules"])
                                ]),
                                _: 1
                              }))
                            : _createCommentVNode("", true),
                          (currentJob.value.scheduleType === 'fixed')
                            ? (_openBlock(), _createBlock(_component_v_col, {
                                key: 1,
                                cols: "12",
                                md: "6"
                              }, {
                                default: _withCtx(() => [
                                  _createVNode(_component_v_text_field, {
                                    modelValue: currentJob.value.fixedTime,
                                    "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((currentJob.value.fixedTime) = $event)),
                                    label: "Fixed Time*",
                                    type: "time",
                                    rules: 
                    currentJob.value.scheduleType === 'fixed' ? [rules.required] : []
                  ,
                                    required: currentJob.value.scheduleType === 'fixed',
                                    hint: "Time to run this job daily (24-hour format)",
                                    "persistent-hint": ""
                                  }, null, 8, ["modelValue", "rules", "required"])
                                ]),
                                _: 1
                              }))
                            : _createCommentVNode("", true),
                          (currentJob.value.scheduleType === 'fixed')
                            ? (_openBlock(), _createBlock(_component_v_col, {
                                key: 2,
                                cols: "12",
                                md: "6"
                              }, {
                                default: _withCtx(() => [
                                  _createVNode(_component_v_select, {
                                    modelValue: currentJob.value.timezone,
                                    "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((currentJob.value.timezone) = $event)),
                                    items: timezoneOptions,
                                    label: "Timezone",
                                    hint: "Timezone for fixed time scheduling",
                                    "persistent-hint": ""
                                  }, null, 8, ["modelValue"])
                                ]),
                                _: 1
                              }))
                            : _createCommentVNode("", true),
                          (currentJob.value.command === 'Wallpaper')
                            ? (_openBlock(), _createBlock(_component_v_col, {
                                key: 3,
                                cols: "12"
                              }, {
                                default: _withCtx(() => [
                                  _createVNode(_component_v_file_input, {
                                    modelValue: wallpaperFile.value,
                                    "onUpdate:modelValue": [
                                      _cache[11] || (_cache[11] = ($event: any) => ((wallpaperFile).value = $event)),
                                      handleWallpaperFileChange
                                    ],
                                    label: "Wallpaper Image (PNG)*",
                                    accept: ".png,image/png",
                                    rules: 
                    currentJob.value.command === 'Wallpaper'
                      ? [rules.required, rules.pngOnly]
                      : []
                  ,
                                    "prepend-icon": "mdi-image",
                                    "show-size": "",
                                    hint: "Select a PNG image file to use as wallpaper",
                                    "persistent-hint": ""
                                  }, null, 8, ["modelValue", "rules"])
                                ]),
                                _: 1
                              }))
                            : _createCommentVNode("", true),
                          _createVNode(_component_v_col, { cols: "12" }, {
                            default: _withCtx(() => [
                              _createVNode(_component_v_switch, {
                                modelValue: currentJob.value.active,
                                "onUpdate:modelValue": _cache[12] || (_cache[12] = ($event: any) => ((currentJob.value.active) = $event)),
                                label: "Active",
                                color: "success",
                                "hide-details": ""
                              }, null, 8, ["modelValue"])
                            ]),
                            _: 1
                          })
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              }),
              _createVNode(_component_v_card_actions, null, {
                default: _withCtx(() => [
                  _createVNode(_component_v_spacer),
                  _createVNode(_component_v_btn, {
                    color: "grey",
                    variant: "text",
                    onClick: closeDialog
                  }, {
                    default: _withCtx(() => _cache[20] || (_cache[20] = [
                      _createTextVNode(" Cancel ")
                    ])),
                    _: 1
                  }),
                  _createVNode(_component_v_btn, {
                    color: "primary",
                    onClick: saveJob,
                    loading: saving.value,
                    disabled: !isFormValid.value
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(isEditing.value ? "Update" : "Create"), 1)
                    ]),
                    _: 1
                  }, 8, ["loading", "disabled"])
                ]),
                _: 1
              })
            ]),
            _: 1
          })
        ]),
        _: 1
      }, 8, ["modelValue"]),
      _createVNode(_component_v_dialog, {
        modelValue: showDeleteDialog.value,
        "onUpdate:modelValue": _cache[15] || (_cache[15] = ($event: any) => ((showDeleteDialog).value = $event)),
        "max-width": "500px"
      }, {
        default: _withCtx(() => [
          _createVNode(_component_v_card, null, {
            default: _withCtx(() => [
              _createVNode(_component_v_card_title, { class: "text-h5" }, {
                default: _withCtx(() => _cache[21] || (_cache[21] = [
                  _createTextVNode("Confirm Delete")
                ])),
                _: 1
              }),
              _createVNode(_component_v_card_text, null, {
                default: _withCtx(() => [
                  _createTextVNode(" Are you sure you want to delete the job \"" + _toDisplayString(jobToDelete.value?.name) + "\"? This action cannot be undone. ", 1)
                ]),
                _: 1
              }),
              _createVNode(_component_v_card_actions, null, {
                default: _withCtx(() => [
                  _createVNode(_component_v_spacer),
                  _createVNode(_component_v_btn, {
                    color: "grey",
                    variant: "text",
                    onClick: _cache[14] || (_cache[14] = ($event: any) => (showDeleteDialog.value = false))
                  }, {
                    default: _withCtx(() => _cache[22] || (_cache[22] = [
                      _createTextVNode(" Cancel ")
                    ])),
                    _: 1
                  }),
                  _createVNode(_component_v_btn, {
                    color: "error",
                    onClick: confirmDelete,
                    loading: deleting.value
                  }, {
                    default: _withCtx(() => _cache[23] || (_cache[23] = [
                      _createTextVNode(" Delete ")
                    ])),
                    _: 1
                  }, 8, ["loading"])
                ]),
                _: 1
              })
            ]),
            _: 1
          })
        ]),
        _: 1
      }, 8, ["modelValue"])
    ]),
    _: 1
  }))
}
}

})